/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { PrinterRestService } from '@gh/builder-shared-data';

import { getSingleNewSelectedEntity, SelectionModel } from '@gh/common-shared';
// @ts-ignore
import { GoogleAuthService } from '@gh/core-external';
import { mapErrorKeys, Message, MessageBasket } from '@gh/core-messages';
import { TranslationService, translationTuple, TranslationTupleOrId } from '@gh/core-mls';
import { confirmationWith, DialogService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { of$, throwError$ } from '@gh/rx';
import { catchError$, filter$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';

import { isEmpty } from 'lodash';
import { Observable } from 'rxjs';

import { printerFinderDialogRoute } from '../components/printer-finder-dialog/printer-finder-dialog.component';
import { PrinterFinderDialogConfig } from '../resources/printer-finder-business.service';
import { PrinterFinderListItem } from '../resources/printer-finder.model';

export type PrinterChooseOptions = {
  finder?: { title: TranslationTupleOrId };
  optional?: boolean;
  optionalMessage?: TranslationTupleOrId;
  cancelMessage?: TranslationTupleOrId;
};

export type PrinterFindAllOptions = {
  optional?: boolean;
  optionalMessage?: TranslationTupleOrId;
};

export type PrinterChooseResult = {
  authCode?: string;
  printer?: PrinterFinderListItem;
};

@Injectable()
export class PrinterBusinessService {
  constructor(private printerRest: PrinterRestService,
              private googleAuth: GoogleAuthService,
              private translations: TranslationService,
              private dialogs: DialogService,
              private waitings: WaitingService) {
  }

  getPrinters(): Observable<any> {
    return this.printerRest.findAll();
  }

  choosePrinter(options: PrinterChooseOptions = {}): Observable<PrinterChooseResult> {
    const optionalMessage = options.optionalMessage ? translationTuple(options.optionalMessage) : void 0;
    const cancelMessage = options.cancelMessage ? translationTuple(options.cancelMessage) : void 0;
    // find all printers
    return this.printerRest.findAllForFinder().pipe(
      this.waitings.operator<PrinterFinderListItem[]>(),
      // verify and choose printer
      switchMap$((printers) => {
        if (isEmpty(printers)) {
          if (options.optional) {
            // tslint:disable-next-line:no-non-null-assertion
            return <any>this.dialogs.confirmation(yesNoConfirmation(optionalMessage!.id, optionalMessage!.params)).pipe(
              filter$(yesAnswer),
              mapTo$(void 0));
          } else {
            return throwError$(MessageBasket.error('msg_no_printer'));
          }
        } else {
          return this.dialogs.openWithInput(printerFinderDialogRoute, <PrinterFinderDialogConfig>{
              title: options.finder && this.translations.get(<any>options.finder.title),
            })
            .afterClosed().pipe(
              filter$((selection) => options.optional ? true : Boolean(selection)),
              map$((selection) => selection ? getSingleNewSelectedEntity<PrinterFinderListItem>(selection) : void 0),
              switchMap$((printer) => printer
                ? of$(printer)
                : (cancelMessage
                  ? this.dialogs.confirmation(yesNoConfirmation(cancelMessage.id, cancelMessage.params)).pipe(
                    filter$(yesAnswer),
                    mapTo$(void 0))
                  : of$(void 0))));
        }
      }),
      // return found printer
      switchMap$((printer?: PrinterFinderListItem) => {
        const authCode$ = printer ? this.grantGoogleOfflineAccess() : of$<any>(void 0);
        return authCode$.pipe(map$((authCode) => ({ authCode, printer })));
      }));
  }

  findAllPrinters(options: PrinterFindAllOptions = {}): Observable<PrinterFinderListItem[]> {
    const optionalMessage = options.optionalMessage ? translationTuple(options.optionalMessage) : void 0;
    // find all printers
    return this.printerRest.findAllForFinder().pipe(
      this.waitings.operator<PrinterFinderListItem[]>(),
      // verify and choose printer
      switchMap$((printers) => {
        if (isEmpty(printers)) {
          if (options.optional) {
            // tslint:disable-next-line:no-non-null-assertion
            return this.dialogs.confirmation(yesNoConfirmation(optionalMessage!.id, optionalMessage!.params)).pipe(
              filter$(yesAnswer),
              mapTo$([]));
          } else {
            return throwError$(Message.error('msg_no_printer'));
          }
        } else {
          return [printers];
        }
      }));
  }

  openPrinterFinder(config?: PrinterFinderDialogConfig): Observable<PrinterFinderListItem> {
    return this.printerRest.findAllForFinder().pipe(
      this.waitings.operator<PrinterFinderListItem[]>(),
      switchMap$((printers) => {
        if (isEmpty(printers)) {
          return throwError$(MessageBasket.error('msg_no_printer'));
        } else {
          return this.dialogs.openWithInput(printerFinderDialogRoute, config || {})
            .afterClosed().pipe(
            filter$(Boolean),
            map$<SelectionModel<PrinterFinderListItem>, PrinterFinderListItem>(getSingleNewSelectedEntity));
        }
      }));
  }

  grantGoogleOfflineAccess(): Observable<boolean> {
    return this.googleAuth.grantOfflineAccess().pipe(
      catchError$((error: MessageBasket) => {
        if (error.has('msg_google_auth:popup_blocked_by_browser')) {
          return this.dialogs.confirmation(confirmationWith(
            yesNoConfirmation('msg_google_consent_screen_blocked'),
            { yesLabel: this.translations.get('lbl_continue') })).pipe(
            filter$(yesAnswer),
            switchMap$(() => this.googleAuth.grantOfflineAccess()));
        } else {
          return throwError$(error);
        }
      }),
      catchError$(mapErrorKeys<boolean>({
        'msg_google_auth:access_denied': 'msg_google_auth_print:access_denied',
      })));
  }
}
